import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { featureCodes } from 'constants/featureCodes';

import { getEligibleVisitorTiers } from '../helpers/getEligibleVisitorTiers';

export const useVisitorFeature = (orgId: number) => {
  // Get active user count
  const { data: activeUserCount = 0 } = useOpQuery({
    apiEndpointName: 'listUsers',
    parameters: [
      orgId,
      {
        filter: 'status:(=A)',
        limit: 1, // We only need filteredCount, not the actual users
      },
    ],
    select: (res) => res.json.filteredCount ?? 0,
  });

  const { data: org } = useOpQuery({
    apiEndpointName: 'describeOrg',
    parameters: [orgId],
    select: (heliumResponse) => heliumResponse.json.data,
  });

  // Determine eligible tiers based on active user count
  const eligibleTiers = getEligibleVisitorTiers(activeUserCount);

  // Find all visitor tier features
  const smallFeature = org?.billableFeatures?.find(
    ({ feature }) => feature?.id === featureCodes.visitorSmall,
  );
  const mediumFeature = org?.billableFeatures?.find(
    ({ feature }) => feature?.id === featureCodes.visitorMedium,
  );
  const largeFeature = org?.billableFeatures?.find(
    ({ feature }) => feature?.id === featureCodes.visitorLarge,
  );

  // Calculate total licenses purchased (only count eligible tiers)
  const totalLicenses =
    (eligibleTiers.includes('SMALL') ? (smallFeature?.hardLimit ?? 0) : 0) +
    (eligibleTiers.includes('MEDIUM') ? (mediumFeature?.hardLimit ?? 0) : 0) +
    (eligibleTiers.includes('LARGE') ? (largeFeature?.hardLimit ?? 0) : 0);

  // Determine the highest purchased tier's user limit
  const purchasedTierUserLimit = (() => {
    if ((largeFeature?.hardLimit ?? 0) > 0) return Infinity;
    if ((mediumFeature?.hardLimit ?? 0) > 0) return 500;
    if ((smallFeature?.hardLimit ?? 0) > 0) return 100;
    return 0;
  })();

  // Check if user count exceeds the purchased tier limit
  const isUserCountExceeded = activeUserCount > purchasedTierUserLimit;

  return {
    activeUserCount,
    totalLicenses,
    isUserCountExceeded,
    purchasedTierUserLimit,
  };
};
